/**
 * Modal de création rapide d'un sous-traitant
 * Utilisé depuis le modal coûts annexes pour créer un ST à la volée
 */

import React, { useState } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useSousTraitants } from '@/hooks/useSousTraitants';
import { SPECIALITES_ST } from '@/types/sous-traitant';
import type { SousTraitant, SpecialiteST, SousTraitantCreate } from '@/types/sous-traitant';
import { useToast } from '@/hooks/use-toast';

// Schéma de validation
const quickCreateSchema = z.object({
  nom: z.string().min(1, 'Le nom est obligatoire').max(100, '100 caractères max'),
  specialite: z.string().optional(),
  telephone: z.string().optional(),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
});

interface SousTraitantQuickCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entrepriseId: string;
  onSuccess: (newST: SousTraitant) => void;
}

export default function SousTraitantQuickCreate({
  open,
  onOpenChange,
  entrepriseId,
  onSuccess,
}: SousTraitantQuickCreateProps) {
  const { toast } = useToast();
  useModalProtection(open);
  const { createSousTraitant } = useSousTraitants(entrepriseId);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    specialite: '',
    telephone: '',
    email: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const result = quickCreateSchema.safeParse(formData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: 'Données invalides',
        description: firstError.message,
        variant: 'destructive',
      });
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const dataToCreate: SousTraitantCreate = {
        entreprise_id: entrepriseId,
        nom: formData.nom.trim(),
        specialite: formData.specialite ? (formData.specialite as SpecialiteST) : null,
        telephone: formData.telephone.trim() || null,
        email: formData.email.trim() || null,
      };

      const newST = await createSousTraitant(dataToCreate);

      if (newST) {
        // Reset form
        setFormData({
          nom: '',
          specialite: '',
          telephone: '',
          email: '',
        });
        onSuccess(newST);
      }
    } catch (error) {
      console.error('[SousTraitantQuickCreate] Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        nom: '',
        specialite: '',
        telephone: '',
        email: '',
      });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau sous-traitant</DialogTitle>
          <DialogDescription>
            Ajoutez rapidement un sous-traitant à votre carnet d'adresses
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nom (requis) */}
          <div className="space-y-2">
            <Label htmlFor="st-nom">
              Nom / Raison sociale <span className="text-destructive">*</span>
            </Label>
            <Input
              id="st-nom"
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              placeholder="Ex: Dupont Électricité"
              required
            />
          </div>

          {/* Spécialité */}
          <div className="space-y-2">
            <Label htmlFor="st-specialite">Spécialité</Label>
            <Select
              value={formData.specialite}
              onValueChange={(value) => setFormData({ ...formData, specialite: value })}
            >
              <SelectTrigger id="st-specialite">
                <SelectValue placeholder="Sélectionner une spécialité" />
              </SelectTrigger>
              <SelectContent>
                {SPECIALITES_ST.map((spec) => (
                  <SelectItem key={spec.value} value={spec.value}>
                    {spec.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Téléphone */}
          <div className="space-y-2">
            <Label htmlFor="st-telephone">Téléphone</Label>
            <Input
              id="st-telephone"
              type="tel"
              value={formData.telephone}
              onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
              placeholder="Ex: 06 12 34 56 78"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="st-email">Email</Label>
            <Input
              id="st-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Ex: contact@dupont-elec.fr"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
